import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Camera, CheckCircle2, ChevronLeft, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { confirmDelivery, fetchDriverOrders, type DriverOrder } from "@/lib/driverApi";
import { getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad, type SignaturePadHandle } from "@/components/driver/SignaturePad";

function nowLocalIsoMinutes(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  const tzOffsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

export default function DriverConfirmDelivery() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<DriverOrder | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [nfNumber, setNfNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [deliveredAt, setDeliveredAt] = useState(nowLocalIsoMinutes());
  const [truckTemperature, setTruckTemperature] = useState("");
  const [receiverName, setReceiverName] = useState("");
  const [receiverDocument, setReceiverDocument] = useState("");
  const [receiptPhoto, setReceiptPhoto] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signatureRef = useRef<SignaturePadHandle>(null);

  const photoPreviewUrl = useMemo(
    () => (receiptPhoto ? URL.createObjectURL(receiptPhoto) : null),
    [receiptPhoto],
  );
  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  useEffect(() => {
    if (!id) return;
    fetchDriverOrders()
      .then((list) => {
        const found = list.find((o) => o.id === id);
        if (!found) {
          setLoadError("Pedido nao encontrado ou nao esta em rota.");
        } else {
          setOrder(found);
          // NF da conferência de expedição é a fonte da verdade: pré-preenche e trava.
          if (found.conferenceNf) setNfNumber(found.conferenceNf);
        }
      })
      .catch((err) => setLoadError(getApiErrorMessage(err)));
  }, [id]);

  const clearPhoto = () => {
    setReceiptPhoto(null);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = () => {
    if (!nfNumber.trim() || !driverName.trim() || !receiverName.trim() || !receiverDocument.trim()) {
      return "Preencha todos os campos obrigatorios.";
    }
    if (!truckTemperature.trim()) return "Temperatura obrigatoria.";
    if (!receiptPhoto) return "Foto do canhoto obrigatoria.";
    if (!signatureRef.current || signatureRef.current.isEmpty()) return "Assinatura do conferente obrigatoria.";
    return null;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!id) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const sigBlob = await signatureRef.current!.toBlob();
    if (!sigBlob) {
      setError("Falha ao capturar assinatura.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmDelivery({
        orderId: id,
        nfNumber: nfNumber.trim(),
        driverName: driverName.trim(),
        deliveredAt: new Date(deliveredAt).toISOString(),
        truckTemperature: truckTemperature.replace(",", "."),
        receiverName: receiverName.trim(),
        receiverDocument: receiverDocument.trim(),
        signature: sigBlob,
        receiptPhoto: receiptPhoto!,
      });
      toast.success("Entrega confirmada", {
        icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
      });
      navigate("/entregador", { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-3">
        <BackLink />
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {loadError}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{order.clientName}</CardTitle>
          {(order.deliveryWindow || order.deliveryDate) && (
            <p className="text-xs text-muted-foreground">{order.deliveryWindow || order.deliveryDate}</p>
          )}
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField id="nf" label="Numero da NF *">
                <Input
                  id="nf"
                  value={nfNumber}
                  onChange={(e) => setNfNumber(e.target.value)}
                  required
                  readOnly={!!order?.conferenceNf}
                  className={order?.conferenceNf ? "bg-muted" : undefined}
                />
                {order?.conferenceNf && (
                  <p className="mt-1 text-xs text-muted-foreground">NF da conferência de expedição (não editável).</p>
                )}
              </FormField>
              <FormField id="driver" label="Nome do motorista *">
                <Input id="driver" value={driverName} onChange={(e) => setDriverName(e.target.value)} required />
              </FormField>
              <FormField id="when" label="Data e hora *">
                <Input
                  id="when"
                  type="datetime-local"
                  value={deliveredAt}
                  onChange={(e) => setDeliveredAt(e.target.value)}
                  required
                />
              </FormField>
              <FormField id="temp" label="Temperatura do caminhao (°C) *">
                <Input
                  id="temp"
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={truckTemperature}
                  onChange={(e) => setTruckTemperature(e.target.value)}
                  required
                />
              </FormField>
              <FormField id="rname" label="Nome do conferente *">
                <Input id="rname" value={receiverName} onChange={(e) => setReceiverName(e.target.value)} required />
              </FormField>
              <FormField id="rdoc" label="Documento do conferente *">
                <Input
                  id="rdoc"
                  value={receiverDocument}
                  onChange={(e) => setReceiverDocument(e.target.value)}
                  required
                />
              </FormField>
            </div>

            <div className="space-y-1">
              <Label>Assinatura do conferente *</Label>
              <SignaturePad ref={signatureRef} />
            </div>

            <div className="space-y-2">
              <Label>Foto do canhoto assinado *</Label>
              <input
                ref={cameraInputRef}
                id="photo"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setReceiptPhoto(e.target.files?.[0] ?? null)}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setReceiptPhoto(e.target.files?.[0] ?? null)}
              />
              {photoPreviewUrl && (
                <div className="relative inline-block">
                  <img
                    src={photoPreviewUrl}
                    alt="Pré-visualização do canhoto"
                    className="max-h-48 rounded-md border"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute right-1 top-1 h-7 w-7"
                    onClick={clearPhoto}
                    aria-label="Remover foto"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => cameraInputRef.current?.click()} className="gap-2">
                  <Camera className="h-4 w-4" />
                  Tirar foto
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Escolher arquivo
                </Button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar entrega
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/entregador" className="inline-flex items-center text-sm text-muted-foreground">
      <ChevronLeft className="h-4 w-4" /> Voltar
    </Link>
  );
}

function FormField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
